import React from "react";

import { GoalBasedFinancialPlanSingleResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";

import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import styles from "./PlanSummary.module.css";

interface Props {
  singleGoalBasedFinancialPlan: GoalBasedFinancialPlanSingleResponse;
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanSummary: React.FC<Props> = ({
  singleGoalBasedFinancialPlan,
  clientSpouseData,
}): JSX.Element => {
  const { goals, accounts, savings, income, expenses, insurance } =
    singleGoalBasedFinancialPlan;

  const { client, spouse } = clientSpouseData;

  const { firstName: clientFirstName } = client;

  const spouseFirstName = spouse?.firstName ?? "Spouse";

  const getNameText = () => {
    if (spouse === undefined) {
      return `${clientFirstName}'s Retirement Plan`;
    }

    return `${clientFirstName} & ${spouseFirstName}'s Retirement Plan`;
  };

  return (
    <BodyContentFormatter>
      <div className={styles.centering}>
        <BorderBox className={styles.borderBox}>
          <Text variant="title2" color="text">
            Plan Summary
          </Text>

          <BorderBox className={styles.borderBoxInner}>
            <div className={styles.row}>
              <Text variant="body" color="text">
                Name
              </Text>

              <Text variant="body" color="text">
                {getNameText()}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Goals
              </Text>

              <Text variant="body" color="text">
                {pluralize(goals.length, "goal", true)}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Assets
              </Text>

              <Text variant="body" color="text">
                {pluralize(accounts.length, "account", true)}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Income
              </Text>

              <Text variant="body" color="text">
                {pluralize(income.length, "income source", true)}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Savings
              </Text>

              <Text variant="body" color="text">
                {pluralize(savings.length, "savings account", true)}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Expenses
              </Text>

              <Text variant="body" color="text">
                {pluralize(expenses.length, "expense", true)}
              </Text>
            </div>

            <div className={styles.row}>
              <Text variant="body" color="text">
                Insurance
              </Text>

              <Text variant="body" color="text">
                {pluralize(insurance.length, "policy", true)}
              </Text>
            </div>
          </BorderBox>
        </BorderBox>
      </div>
    </BodyContentFormatter>
  );
};

export default FinancialPlanSummary;
